/**
 * Main theme entry point
 * Initializes all global dependencies including lazy loading, carousels, custom elements, cart, and search
 */

import LazyLoad, { ILazyLoadInstance } from 'vanilla-lazyload'

import 'flickity'
import 'flickity/dist/flickity.min.css'

import 'styles/theme.scss'

import '@/components/custom-elements/registerCustomElements'

import 'libs/athos/athos-init'

import { cart, Cart } from '../helpers/cart'
import { session } from '../helpers/session'
import { capLazySizes } from '../helpers/image-density'

declare global {
  interface Window {
    Cart: Cart | null;
    Session: typeof session;
    lazyLoadInstance: ILazyLoadInstance;
  }
}

window.Cart = cart
window.Session = session

// Lazy images carry a 5px placeholder as their `src` until LazyLoad swaps in the
// real one, and `unobserve_completed` (on by default) drops an element as soon as
// it errors. A single transient CDN 5xx therefore leaves that placeholder on
// screen permanently, which reads as a blurry image. Restore the original
// attributes on error and re-queue the element a couple of times with backoff.
const IMAGE_RETRY_DELAYS_MS = [1000, 3000]
const imageRetries = new WeakMap<HTMLElement, number>()

window.lazyLoadInstance = new LazyLoad({
  restore_on_error: true,
  callback_enter: (el) => capLazySizes(el as HTMLElement),
  callback_error: (el) => {
    const attempt = imageRetries.get(el) ?? 0
    const delay = IMAGE_RETRY_DELAYS_MS[attempt]
    if (delay === undefined) return

    imageRetries.set(el, attempt + 1)
    window.setTimeout(() => {
      LazyLoad.resetStatus(el)
      window.lazyLoadInstance.update()
    }, delay)
  }
})
window.lazyLoadInstance.update()

// Omneo loyalty — only pages rendering a widget mount download the chunk
if (
  document.querySelector(
    'omneo-membership-rank, omneo-loyalty-rewards, omneo-loyalty-achievements, omneo-reward-announcement'
  )
) {
  import('libs/omneo/omneo-init').then(({ initOmneo }) => initOmneo())
}

// Swym wishlist — only pages rendering a wishlist mount download the chunk.
// Card hearts (`.swym-card-button`) render later via Preact, so they can't be
// matched here; the header count element covers every page where wishlist is
// enabled and is present at execution time, so it alone triggers the init.
if (document.querySelector('x-wishlist-count, x-wishlist-toggle, x-wishlist-page')) {
  import('libs/swym/swym-init').then(({ initSwym }) => initSwym())
}

// Shared-wishlist landing page (/pages/swym-share-wishlist) — separate chunk
// since it never needs the ResultsCard graph swym-init.ts pulls in.
if (document.getElementById('swym-shared-wishlist-render-container')) {
  import('libs/swym/swym-share-init').then(({ initSwymShare }) => initSwymShare())
}
